import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Search,
  Edit,
  Users,
  CheckCircle,
  XCircle,
  QrCode,
  Database,
  ShieldCheck,
  ClipboardList,
  Clock,
  Ban,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import QRCodeModal from "../../../components/QRCodeModal/QRCodeModal";
import {
  getAllVisitors,
  checkInVisitor,
  checkOutVisitor,
  blacklistVisitor,
  unblacklistVisitor,
  verifyVisitorIdentity,
  seedSampleVisitors,
  generateVisitorQRCode,
} from "../../../service/securityService";
import { Visitor, SecuritySearchFilters, PaginationParams } from "../../../types/security_types";
import VisitorForm from "./VisitorForm";

function isVisitorBlacklisted(v: Visitor): boolean {
  return !!(v.isBlacklisted || v.blacklisted || v.status === "Blacklisted");
}

function formatVisitWindow(v: Visitor): string {
  const now = Date.now();
  const start = new Date(v.visitStart).getTime();
  const end = new Date(v.visitEnd).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return "—";
  if (now < start) {
    const mins = Math.ceil((start - now) / 60000);
    return mins < 120 ? `Starts in ${mins}m` : `Starts in ${Math.round(mins / 60)}h`;
  }
  if (now > end) return "Outside window";
  const mins = Math.ceil((end - now) / 60000);
  return mins < 120 ? `${mins}m left` : `${Math.round(mins / 60)}h left`;
}

const VisitorManagement: React.FC = () => {
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL Regions");
  const [selectedPremises, setSelectedPremises] = useState<string>("ALL Premises");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 30,
  });
  const [seeding, setSeeding] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<any>(null);
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    fetchVisitors();
  }, [searchQuery, selectedRegion, selectedPremises, pagination]);

  const fetchVisitors = async () => {
    try {
      setLoading(true);
      const filters: SecuritySearchFilters = {};
      if (selectedRegion !== "ALL Regions") filters.region = selectedRegion;
      if (selectedPremises !== "ALL Premises") filters.premise = selectedPremises;
      if (searchQuery.trim()) filters.searchQuery = searchQuery;

      const response = await getAllVisitors(filters, pagination);
      setVisitors(response.data || []);
    } catch (error: any) {
      console.error("Error fetching visitors:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Failed to load visitors"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedVisitor(null);
    setIsCreateModalOpen(true);
  };

  const handleEdit = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setIsEditModalOpen(true);
  };

  const handleCheckIn = async (visitor: Visitor) => {
    try {
      const id = visitor._id || visitor.id;
      if (!id) return;
      await checkInVisitor(id);
      toast.success("Visitor checked in successfully");
      fetchVisitors();
    } catch (error: any) {
      console.error("Error checking in visitor:", error);
      toast.error(error.response?.data?.resMessage || "Failed to check in visitor");
    }
  };

  const handleCheckOut = async (visitor: Visitor) => {
    try {
      const id = visitor._id || visitor.id;
      if (!id) return;
      await checkOutVisitor(id);
      toast.success("Visitor checked out successfully");
      fetchVisitors();
    } catch (error: any) {
      console.error("Error checking out visitor:", error);
      toast.error(error.response?.data?.resMessage || "Failed to check out visitor");
    }
  };

  const handleBlacklist = async (visitor: Visitor) => {
    const id = visitor._id || visitor.id;
    if (!id) return;

    if (isVisitorBlacklisted(visitor)) {
      if (!window.confirm(`Remove "${visitor.fullName}" from blacklist?`)) return;
      try {
        await unblacklistVisitor(String(id));
        toast.success("Visitor removed from blacklist");
        fetchVisitors();
      } catch (error: any) {
        console.error(error);
        toast.error(
          error.response?.data?.message ||
            error.response?.data?.resMessage ||
            "Failed to update blacklist"
        );
      }
      return;
    }

    const reason = window.prompt(`Blacklist "${visitor.fullName}" — reason (optional):`, "");
    if (reason === null) return;
    if (!window.confirm(`Blacklist ${visitor.fullName}?`)) return;

    try {
      await blacklistVisitor(String(id), reason || undefined);
      toast.success("Visitor blacklisted");
      fetchVisitors();
    } catch (error: any) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Failed to blacklist visitor"
      );
    }
  };

  const handleVerifyId = async (visitor: Visitor) => {
    const id = visitor._id || visitor.id;
    if (!id) return;
    const notes = window.prompt("ID verification notes (optional):", "");
    if (notes === null) return;
    try {
      await verifyVisitorIdentity(String(id), notes || undefined);
      toast.success("ID verification recorded");
      fetchVisitors();
    } catch (error: any) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Verification failed"
      );
    }
  };

  const handleSeedSample = async () => {
    if (!window.confirm("Add sample visitor Ravi Kumar (Vendor, Biomedical) if not present?")) {
      return;
    }
    try {
      setSeeding(true);
      const { inserted } = await seedSampleVisitors();
      toast.success(
        inserted > 0
          ? "Sample visitor added (ensure access zones exist)."
          : "Sample already exists or could not be created."
      );
      fetchVisitors();
    } catch (error: any) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Could not add sample visitor"
      );
    } finally {
      setSeeding(false);
    }
  };

  const handleFormSuccess = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedVisitor(null);
    fetchVisitors();
  };

  const handleQRCode = useCallback(
    async (
      visitorId: string,
      action: "preview" | "download" = "preview",
      format: "png" | "svg" = "png"
    ) => {
      try {
        if (action === "download") {
          try {
            const blob = await generateVisitorQRCode(visitorId, true, format);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `visitor-${visitorId}-badge.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("QR Code badge downloaded successfully");
          } catch (error: any) {
            // If backend download fails, generate on frontend
            const response = await generateVisitorQRCode(visitorId, false);
            const QRCode = require('react-qr-code');
            // For download, we'll need to convert QR to image - simplified for now
            toast.info("Please use preview and right-click to save, or install qrcode library in backend");
          }
        } else {
          const response = await generateVisitorQRCode(visitorId, false);
          const visitor = visitors.find((v) => (v._id || v.id) === visitorId);
          
          // Generate QR code image on frontend if backend doesn't provide it
          let qrCodeImage = response.qrCodeImage;
          if (!qrCodeImage && response.qrData) {
            // Use react-qr-code to generate image data URL
            const QRCode = require('react-qr-code');
            // We'll render it in the modal instead
            qrCodeImage = null; // Will be generated in modal
          }
          
          setQrCodeData({
            ...response,
            qrCodeImage: qrCodeImage,
            visitorName: visitor?.fullName || "Visitor",
            qrData: {
              ...response.qrData,
              visitorId: visitorId,
            },
          });
          setShowQRModal(true);
        }
      } catch (error: any) {
        console.error("Error with QR code:", error);
        toast.error(`Failed to ${action} QR code badge`);
      }
    },
    [visitors]
  );

  return (
    <div className="px-2 py-4 w-full h-full mt-10">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-3">
            <div className="bg-[#EAFBFA] p-3 rounded-lg">
              <Users className="w-6 h-6 text-[#0fa9a6]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Visitor & Vendor Management
              </h1>
              <p className="text-gray-600 max-w-3xl">
                Pre-registration, ID verification, QR badges, host &amp; purpose mapping,
                time-bound visits, and blacklist — integrated with security access zones and mobile
                validation APIs.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSeedSample}
              disabled={seeding}
              className="flex items-center gap-2 px-4 py-2 border border-[#0fa9a6] text-[#0fa9a6] rounded-full hover:bg-[#EAFBFA] transition-colors disabled:opacity-50"
            >
              <Database className="w-5 h-5" />
              Sample data
            </button>
            <button
              type="button"
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full hover:bg-teal-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Pre-register
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        {[
          { icon: ClipboardList, t: "Pre-registration", d: "Create visits before arrival" },
          { icon: ShieldCheck, t: "ID verification", d: "Desk check + audit trail" },
          { icon: QrCode, t: "Badge QR", d: "Encode pass & window" },
          { icon: Users, t: "Host mapping", d: "Dept + contact" },
          { icon: Clock, t: "Visit window", d: "Check-in within SLA" },
          { icon: Ban, t: "Blacklist", d: "Block at gate" },
        ].map((x) => (
          <div
            key={x.t}
            className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm text-sm"
          >
            <x.icon className="w-5 h-5 text-[#0fa9a6] mb-1" />
            <p className="font-semibold text-gray-900">{x.t}</p>
            <p className="text-xs text-gray-500">{x.d}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <input
                type="text"
                placeholder="Search visitors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
          </div>
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedRegion}
              onChange={setSelectedRegion}
              type="region"
            />
          </div>
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
          </div>
        </div>
      </div>

      {/* Visitors List */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <SpinLoader />
          </div>
        ) : visitors.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-500">
            <Users className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No visitors found</p>
            <p className="text-sm">
              {searchQuery || selectedRegion !== "ALL Regions"
                ? "Try adjusting your search or filters"
                : "Pre-register your first visitor"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="py-3 px-4 font-semibold text-gray-700">Name</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Type</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Mobile</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Host Department</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Visit window</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Time left</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">ID ok</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Status</th>
                  <th className="py-3 px-4 font-semibold text-gray-700 text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {visitors.map((visitor) => (
                  <tr
                    key={visitor._id || visitor.id}
                    className={`border-b hover:bg-gray-50 ${
                      isVisitorBlacklisted(visitor) ? "bg-red-50" : ""
                    }`}
                  >
                    <td className="py-3 px-4 font-medium text-gray-900">
                      {visitor.fullName}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{visitor.visitorType}</td>
                    <td className="py-3 px-4 text-gray-600">{visitor.mobileNumber}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {visitor.hostDepartment}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-xs">
                      {new Date(visitor.visitStart).toLocaleString()} —{" "}
                      {new Date(visitor.visitEnd).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-gray-700 text-sm font-medium">
                      {formatVisitWindow(visitor)}
                    </td>
                    <td className="py-3 px-4">
                      {visitor.idVerified ? (
                        <span className="text-emerald-700 text-xs font-semibold">Yes</span>
                      ) : (
                        <span className="text-amber-700 text-xs">Pending</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {isVisitorBlacklisted(visitor) ? (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                          Blacklisted
                        </span>
                      ) : visitor.checkedIn && !visitor.checkedOut ? (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                          Checked In
                        </span>
                      ) : visitor.checkedOut ? (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                          Checked Out
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                          {visitor.status === "Pre-registered" || !visitor.checkedIn
                            ? "Pre-registered"
                            : "Pending"}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleQRCode(visitor._id || visitor.id || "", "preview")}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Badge QR"
                        >
                          <QrCode className="w-5 h-5" />
                        </button>
                        {!visitor.idVerified && !isVisitorBlacklisted(visitor) && (
                          <button
                            type="button"
                            onClick={() => handleVerifyId(visitor)}
                            className="p-2 text-teal-700 hover:bg-teal-50 rounded-lg transition-colors"
                            title="Record ID verification"
                          >
                            <ShieldCheck className="w-5 h-5" />
                          </button>
                        )}
                        {!visitor.checkedIn && !isVisitorBlacklisted(visitor) && (
                          <button
                            onClick={() => handleCheckIn(visitor)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Check In"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                        )}
                        {visitor.checkedIn && !visitor.checkedOut && !isVisitorBlacklisted(visitor) && (
                          <button
                            onClick={() => handleCheckOut(visitor)}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Check Out"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(visitor)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleBlacklist(visitor)}
                          className={`p-2 rounded-lg transition-colors ${
                            isVisitorBlacklisted(visitor)
                              ? "text-green-600 hover:bg-green-50"
                              : "text-red-600 hover:bg-red-50"
                          }`}
                          title={
                            isVisitorBlacklisted(visitor) ? "Remove from blacklist" : "Blacklist"
                          }
                        >
                          <Ban className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedVisitor(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <VisitorForm
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsCreateModalOpen(false);
              setSelectedVisitor(null);
            }}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsCreateModalOpen(false);
              setSelectedVisitor(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="visitorForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Save
          </button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedVisitor(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <VisitorForm
            visitor={selectedVisitor || undefined}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedVisitor(null);
            }}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsEditModalOpen(false);
              setSelectedVisitor(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="visitorForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Update
          </button>
        </div>
      </Modal>

      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        qrCodeData={qrCodeData}
        onDownloadPNG={() => handleQRCode(qrCodeData?.qrData?.visitorId, "download", "png")}
        onDownloadSVG={() => handleQRCode(qrCodeData?.qrData?.visitorId, "download", "svg")}
      />
    </div>
  );
};

export default VisitorManagement;

